/**
 * @fileoverview Telegram Mini App API сервис для интеграции с Telegram.
 * Обеспечивает полную интеграцию с Telegram Web App SDK v3,
 * включая управление главной кнопкой, попапами, темой и пользовательскими данными.
 * 
 * @since 1.0.0
 */

import {
  closeMiniApp,
  init,
  initData,
  miniApp,
  retrieveLaunchParams,
  showPopup,
  mainButton as tgMainButton,
  themeParams,
  viewport
} from '@telegram-apps/sdk-react';
import { logger } from './logger';

/**
 * Сервис для работы с Telegram Mini App API на основе современного SDK v3.
 * 
 * Обеспечивает полноценную интеграцию с Telegram экосистемой, включая:
 * - Инициализацию и настройку Mini App
 * - Управление главной кнопкой (состояние, обработчики)
 * - Показ попапов и диалогов подтверждения
 * - Доступ к данным пользователя и чата
 * - Интеграцию с темой Telegram
 * - Управление вьюпортом и жизненным циклом приложения
 * 
 * **Ключевые особенности:**
 * - Использует только официальный @telegram-apps/sdk-react v3
 * - Не обращается к window.Telegram (устаревший API)
 * - Обеспечивает fallback механизмы для неподдерживаемых функций
 * - Полное логирование ошибок через Sentry
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * // Базовая инициализация приложения
 * import { telegramApi } from './utils/telegramApi';
 * 
 * useEffect(() => {
 *   const initTelegram = async () => {
 *     try {
 *       await telegramApi.initApp();
 *       console.log('Приложение инициализировано');
 *     } catch (error) {
 *       console.error('Ошибка инициализации:', error);
 *     }
 *   };
 *   initTelegram();
 * }, []);
 * ```
 * 
 * @see {@link https://docs.telegram-mini-apps.com/} для Telegram Mini Apps документации
 */
class TelegramApiService {
  private isInitialized = false;
  private currentMainButtonHandler: (() => void) | null = null;

  /**
   * Объект API для управления главной кнопкой Telegram Mini App.
   * 
   * Предоставляет совместимый с легаси-кодом API для работы с основной кнопкой.
   * Включает методы для показа/скрытия, управления состоянием и обработки кликов.
   * 
   * @since 1.0.0
   */
  mainButton = {
    show: (text: string, enabled: boolean = true) => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({
            text,
            isVisible: true,
            isEnabled: enabled
          });
          setTimeout(() => {
            if (tgMainButton.setParams.isAvailable()) {
              tgMainButton.setParams({
                text,
                isVisible: true,
                isEnabled: enabled
              });
            }
          }, 0);
          requestAnimationFrame(() => {
            if (tgMainButton.setParams.isAvailable()) {
              tgMainButton.setParams({
                text,
                isVisible: true,
                isEnabled: enabled
              });
            }
          });
        }
      } catch (error) {
        logger.error('Ошибка при показе главной кнопки', { error, text, enabled });
      }
    },
    hide: () => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({ isVisible: false });
        }
        if (this.currentMainButtonHandler) {
          if (tgMainButton.offClick.isAvailable()) {
            tgMainButton.offClick(this.currentMainButtonHandler);
          }
          this.currentMainButtonHandler = null;
        }
      } catch (error) {
        logger.error('Ошибка при скрытии главной кнопки', { error });
      }
    },
    enable: () => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({ isEnabled: true });
        }
      } catch (error) {
        logger.error('Ошибка при включении главной кнопки', { error });
      }
    },
    disable: () => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({ isEnabled: false });
        }
      } catch (error) {
        logger.error('Ошибка при отключении главной кнопки', { error });
      }
    },
    setText: (text: string) => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({ text });
        }
      } catch (error) {
        logger.error('Ошибка при изменении текста главной кнопки', { error, text });
      }
    },
    onClick: (handler: () => void) => {
      try {
        if (this.currentMainButtonHandler) {
          if (tgMainButton.offClick.isAvailable()) {
            tgMainButton.offClick(this.currentMainButtonHandler);
          }
        }
        if (tgMainButton.onClick.isAvailable()) {
          this.currentMainButtonHandler = handler;
          tgMainButton.onClick(handler);
          return () => {
            if (this.currentMainButtonHandler === handler) {
              if (tgMainButton.offClick.isAvailable()) {
                tgMainButton.offClick(handler);
              }
              this.currentMainButtonHandler = null;
            }
          };
        }
        return () => {};
      } catch (error) {
        logger.error('Ошибка при установке обработчика главной кнопки', { error });
        return () => {};
      }
    },
    offClick: () => {
      try {
        if (this.currentMainButtonHandler && tgMainButton.offClick.isAvailable()) {
          tgMainButton.offClick(this.currentMainButtonHandler);
          this.currentMainButtonHandler = null;
        }
      } catch (error) {
        logger.error('Ошибка при удалении обработчика главной кнопки', { error });
      }
    },
    reset: () => {
      try {
        if (tgMainButton.setParams.isAvailable()) {
          tgMainButton.setParams({ isVisible: false });
        }
        if (this.currentMainButtonHandler && tgMainButton.offClick.isAvailable()) {
          tgMainButton.offClick(this.currentMainButtonHandler);
          this.currentMainButtonHandler = null;
        }
      } catch (error) {
        logger.error('Ошибка при сбросе главной кнопки', { error });
      }
    }
  };

  /**
   * Инициализирует Telegram Mini App с полной настройкой всех компонентов.
   * 
   * Выполняет полную секвенцию инициализации согласно лучшим практикам SDK v3:
   * 1. Инициализация основного SDK
   * 2. Монтирование основных компонентов (MiniApp, MainButton, Viewport)
   * 3. Привязка CSS переменных темы и вьюпорта
   * 4. Отправка сигнала готовности и развёртывание на весь экран
   * 
   * @returns Promise<void> - Разрешается после успешной инициализации
   * 
   * @throws {Error} При ошибках инициализации компонентов SDK
   * 
   * @remarks
   * Метод защищён от повторных вызовов - при повторном вызове
   * немедленно возвращается без выполнения инициализации.
   * 
   * @since 1.0.0
   * 
   * @example
   * ```tsx
   * // Основной сценарий использования
   * useEffect(() => {
   *   const init = async () => {
   *     try {
   *       await telegramApi.initApp();
   *       console.log('Telegram Mini App готов к работе');
   *     } catch (error) {
   *       console.error('Ошибка инициализации:', error);
   *     }
   *   };
   *   init();
   * }, []);
   * ```
   * 
   * @see {@link https://docs.telegram-mini-apps.com/packages/telegram-apps-sdk/} для SDK документации
   */
  async initApp(): Promise<void> {
    // Проверяем, не инициализировано ли уже
    if (this.isInitialized) {
      return;
    }

    try {
      // 1. Инициализация SDK
      init();
      
      // 2. Монтирование компонентов согласно документации SDK v3
      // MiniApp монтирует ThemeParams внутренне
      if (!miniApp.isMounted()) {
        await miniApp.mount();
      }
      
      // 3. Монтируем MainButton и Viewport
      if (!tgMainButton.isMounted()) {
        tgMainButton.mount();
      }
      
      if (!viewport.isMounted() && !viewport.isMounting()) {
        await viewport.mount();
      }
      
      // 4. Привязка CSS переменных
      if (!themeParams.isCssVarsBound()) {
        themeParams.bindCssVars();
      }
      
      if (!viewport.isCssVarsBound()) {
        viewport.bindCssVars();
      }
      
      // 5. Сигнал готовности
      miniApp.ready();

      // 6. Открываем на весь экран
      viewport.expand();
      
      this.isInitialized = true;
      this.logInitializationInfo();
      
    } catch (error) {
      logger.error('Ошибка при инициализации Telegram Mini App', { error });
      throw error;
    }
  }

  /**
   * Логирует детальную информацию о состоянии после инициализации.
   * 
   * Собирает и отправляет в логи ключевую информацию о пользователе,
   * платформе и параметрах запуска для отладки и мониторинга.
   * 
   * @returns void
   * 
   * @remarks
   * Метод использует try-catch для обработки случаев,
   * когда некоторые данные могут быть недоступны.
   * 
   * @since 1.0.0
   * 
   * @private
   */
  private logInitializationInfo(): void {
    try {
      const userData = initData.user();
      
      // Получаем launch parameters
      let launchParams;
      try {
        launchParams = retrieveLaunchParams();
      } catch (launchError) {
        logger.warn('Не удалось получить launch parameters', { error: launchError });
      }
      
      logger.info('Telegram Mini App успешно инициализирован', {
        user: userData ? {
          id: userData.id,
          firstName: userData.first_name,
          lastName: userData.last_name,
          username: userData.username,
          isPremium: userData.is_premium,
        } : null,
        platform: launchParams?.tgWebAppPlatform,
        version: launchParams?.tgWebAppVersion,
        startParam: launchParams?.startParam,
      });
    } catch (error) {
      logger.warn('Не удалось получить информацию об инициализации', { error });
    }
  }

  /**
   * Отображает уведомление пользователю через Telegram API.
   * 
   * Приоритетно использует нативный Telegram попап, при недоступности
   * выполняет fallback к стандартному браузерному alert().
   * 
   * @param message - Текст сообщения для отображения
   * 
   * @returns Promise<void> - Разрешается после закрытия уведомления
   * 
   * @remarks
   * Метод гарантирует показ уведомления любым доступным способом
   * и никогда не выбрасывает ошибки.
   * 
   * @since 1.0.0
   * 
   * @example
   * ```tsx
   * // Показ простого уведомления
   * await telegramApi.showAlert('Фотография сохранена!');
   * 
   * // Показ сообщения об ошибке
   * try {
   *   await uploadPhoto();
   * } catch (error) {
   *   await telegramApi.showAlert(`Ошибка: ${error.message}`);
   * }
   * ```
   */
  async showAlert(message: string): Promise<void> {
    try {
      if (showPopup.isAvailable()) {
        await showPopup({
          title: 'Уведомление',
          message: message,
          buttons: [{ id: 'ok', type: 'ok' }]
        });
      } else {
        logger.warn('showPopup недоступен, используем fallback');
        alert(message);
      }
    } catch (error) {
      logger.error('Ошибка при показе алерта', { error, message });
      // Fallback к стандартному alert
      alert(message);
    }
  }

  /**
   * Отображает диалог подтверждения с кнопками "ОК" и "Отмена".
   * 
   * Приоритетно использует нативный Telegram попап, при недоступности
   * выполняет fallback к стандартному браузерному confirm().
   * 
   * @param message - Текст вопроса для подтверждения
   * 
   * @returns Promise<boolean> - true если пользователь нажал "ОК", false если "Отмена"
   * 
   * @since 1.0.0
   * 
   * @example
   * ```tsx
   * // Подтверждение действия
   * const confirmed = await telegramApi.showConfirm('Удалить все фотографии?');
   * if (confirmed) {
   *   deleteAllPhotos();
   * }
   * 
   * // Подтверждение важного действия
   * if (await telegramApi.showConfirm('Отправить данные на сервер?')) {
   *   await uploadToServer();
   * }
   * ```
   */
  async showConfirm(message: string): Promise<boolean> {
    try {
      if (showPopup.isAvailable()) {
        const result = await showPopup({
          title: 'Подтверждение',
          message: message,
          buttons: [
            { id: 'cancel', type: 'cancel' },
            { id: 'ok', type: 'ok' }
          ]
        });
        return result === 'ok';
      } else {
        logger.warn('showPopup недоступен, используем fallback');
        return confirm(message);
      }
    } catch (error) {
      logger.error('Ошибка при показе диалога подтверждения', { error, message });
      // Fallback к стандартному confirm
      return confirm(message);
    }
  }

  /**
   * Настройка главной кнопки
   */
  setMainButton(text: string, color: string, textColor: string, handler?: () => void): void {
    try {
      // Убираем предыдущий обработчик
      if (this.currentMainButtonHandler) {
        if (tgMainButton.offClick.isAvailable()) {
          tgMainButton.offClick(this.currentMainButtonHandler);
        }
      }

      // Устанавливаем параметры кнопки
      if (tgMainButton.setParams.isAvailable()) {
        tgMainButton.setParams({
          text,
          backgroundColor: color as `#${string}`,
          textColor: textColor as `#${string}`,
          isVisible: true,
          isEnabled: true
        });
      }

      // Устанавливаем новый обработчик
      if (handler && tgMainButton.onClick.isAvailable()) {
        this.currentMainButtonHandler = handler;
        tgMainButton.onClick(handler);
      }
    } catch (error) {
      logger.error('Ошибка при настройке главной кнопки', { error, text, color, textColor });
    }
  }

  /**
   * Скрытие главной кнопки
   */
  hideMainButton(): void {
    try {
      if (tgMainButton.setParams.isAvailable()) {
        tgMainButton.setParams({
          isVisible: false
        });
      }
      
      // Убираем обработчик
      if (this.currentMainButtonHandler) {
        if (tgMainButton.offClick.isAvailable()) {
          tgMainButton.offClick(this.currentMainButtonHandler);
        }
        this.currentMainButtonHandler = null;
      }
    } catch (error) {
      logger.error('Ошибка при скрытии главной кнопки', { error });
    }
  }

  /**
   * Отправка фотографий на сервер
   */
  async sendPhotos(photos: string[], operationData: any, geolocation?: { latitude: number; longitude: number }): Promise<void> {
    try {
      const confirmed = await this.showConfirm('Отправить фотографии на сервер?');
      
      if (confirmed) {
        logger.info('Отправка фотографий на сервер', {
          photosCount: photos.length,
          hasGeolocation: !!geolocation,
          operationData
        });

        // Здесь должна быть логика отправки на сервер
        // Для демонстрации просто показываем сообщение об успехе
        await this.showAlert('Фотографии успешно отправлены!');
      }
    } catch (error) {
      logger.error('Ошибка при отправке фотографий', { error });
      await this.showAlert('Ошибка при отправке фотографий');
    }
  }

  /**
   * Получение данных пользователя
   */
  getUserData() {
    try {
      return initData.user();
    } catch (error) {
      logger.error('Ошибка при получении данных пользователя', { error });
      return null;
    }
  }

  /**
   * Получение данных чата
   */
  getChatData() {
    try {
      return initData.chat();
    } catch (error) {
      logger.error('Ошибка при получении данных чата', { error });
      return null;
    }
  }

  /**
   * Получение параметров темы
   */
  getThemeParams() {
    try {
      return themeParams.state();
    } catch (error) {
      logger.error('Ошибка при получении параметров темы', { error });
      return null;
    }
  }

  /**
   * Закрытие приложения без подтверждения
   */
  closeApp(): void {
    try {
      if (closeMiniApp.isAvailable()) {
        closeMiniApp();
      }
    } catch (error) {
      logger.error('Ошибка при закрытии приложения', { error });
    }
  }

  /**
   * Закрытие приложения с подтверждением
   */
  async close(): Promise<void> {
    try {
      const confirmed = await this.showConfirm('Вы уверены, что хотите закрыть приложение?');
      
      if (confirmed) {
        await this.showAlert('Приложение будет закрыто');
        this.closeApp();
      }
    } catch (error) {
      logger.error('Ошибка при закрытии приложения', { error });
      await this.showAlert('Ошибка при закрытии приложения');
    }
  }

  /**
   * Проверка инициализации
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}

/**
 * Глобальный экземпляр сервиса Telegram API.
 * 
 * Используется как singleton для обеспечения единого состояния
 * инициализации и конфигурации во всём приложении.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * import { telegramApi } from './utils/telegramApi';
 * 
 * // Использование в любом месте приложения
 * await telegramApi.initApp();
 * await telegramApi.showAlert('Привет!');
 * ```
 */
export const telegramApi = new TelegramApiService(); 